

 /**
Store Detail Page JS Optimization & Bug Fixes
I have reviewed and updated the store-detail.js code to improve page performance and fix the carousel loading issue.
The following changes have been done:
Restricted Swiper script loading
Swiper JS and CSS will now load only when the store has multiple images available in storeImagesList.
Previously, the script logic was present but not properly controlled. Now the condition is:
if (storeImagesList.length > 1) {
  loadSwiperLibrary();
}
This avoids unnecessary loading of Swiper on stores where only a single image or no image list is available.
Fixed missing Swiper asset URL issue
The previous JS was using swiperCSSUrl and swiperJSUrl, but these variables were not defined inside the function.
Now the JS correctly reads the URLs from the section attributes:
data-swiper-css="{{ 'swiper-bundle.min.css' | asset_url }}"
data-swiper-js="{{ 'swiper-bundle.min.js' | asset_url }}"
Fixed carousel image rendering issue
The old code was stopping after the first image using this condition:
if (index > 0) return;
Because of this, even when multiple store images were available, only the first image was being inserted.
This has been removed so all valid images from storeImagesList are now added to the carousel.
Added valid image filtering
The updated code now filters the image list before rendering. It checks that:
The image URL exists.
The image URL is valid.
Duplicate images are removed.
This prevents broken or duplicate slides from being added.
Single image fallback improved
If the store has no storeImagesList, or only one valid image, the page now shows the normal single image layout instead of loading the carousel.
This keeps the page lighter and faster.
Restricted Featurable script loading
The Featurable review script now loads only when featurableWidgetId exists.
If the store does not have Featurable widget data, the script is not loaded and the normal review count/rating is displayed.
Improved page speed approach
The main store-detail.js is still loaded because it is required to render the store details from localStorage.
However, heavy third-party or optional scripts are now conditionally loaded only when required:
store-detail.js      → loads on store detail page
Swiper JS/CSS        → loads only when multiple store images exist
Featurable script    → loads only when Featurable widget ID exists
Reduced unnecessary browser work
By avoiding extra scripts where metafield data is blank, the browser downloads fewer files, executes less JavaScript, and avoids unnecessary DOM work.
Conclusion
Yes, this approach will help improve page speed because scripts are now loaded based on actual data availability. If metafields like storeImagesList or featurableWidgetId are blank, the related scripts will not load unnecessarily.

 */
/**
 * Store Detail Data Resolution Strategy
 *
 * Correct priority:
 * URL handle -> embedded Shopify metaobject JSON -> localStorage cache -> render
 *
 * localStorage is no longer the only source of truth.
 * It is now used only for faster repeat rendering after the store has already
 * been resolved once.
 */

(function () {
  'use strict';

  // Configuration - No longer needed for localStorage approach
  const FEATURABLE_SCRIPT_SRC = 'https://featurable.com/assets/bundle.js';
  const FEATURABLE_STYLE_TAG = 'featurable-store-detail-overrides';

  const SWIPER_SECTION_SELECTOR = '.store-detail-section';
  let featurableScriptRequested = false;
  let swiperScriptRequested = false;

  // DOM Elements
  let loaderElement, detailElement, notFoundElement;
  let storeCarouselSwiper = null;
  let carouselPerfObserver = null;

  function startCarouselPerfObserver() {
    if (!('PerformanceObserver' in window)) return;
    stopCarouselPerfObserver();
    try {
      carouselPerfObserver = new PerformanceObserver(function(list) {
        list.getEntries().forEach(function(entry) {
          if (entry.duration > 50) {
            console.warn('[store-detail] Long animation frame detected:', Math.round(entry.duration) + 'ms');
          }
        });
      });
      carouselPerfObserver.observe({ type: 'long-animation-frame', buffered: false });
    } catch (e) {
      carouselPerfObserver = null;
    }
  }

  function stopCarouselPerfObserver() {
    if (carouselPerfObserver) {
      carouselPerfObserver.disconnect();
      carouselPerfObserver = null;
    }
  }

  function isValidHttpUrl(value) {
    return typeof value === 'string' && (value.startsWith('http') || value.startsWith('//'));
  }

  function hasValidStoreImagesList(images) {
    return Array.isArray(images) && images.some(isValidHttpUrl);
  }

  function getValidStoreImages(images) {
    if (!Array.isArray(images)) return [];

    return images.reduce(function(acc, imageUrl) {
      if (isValidHttpUrl(imageUrl) && acc.indexOf(imageUrl) === -1) {
        acc.push(imageUrl);
      }
      return acc;
    }, []);
  }

  function getSwiperAssetUrls() {
    const section = document.querySelector(SWIPER_SECTION_SELECTOR);
    const dataset = section ? section.dataset : {};

    // Prefer Liquid data attributes. Keep safe fallbacks for existing inline variables.
    const jsUrl = dataset.swiperJsUrl
      || dataset.swiperJs
      || (typeof swiperJSUrl !== 'undefined' ? swiperJSUrl : '');

    const cssUrl = dataset.swiperCssUrl
      || dataset.swiperCss
      || (typeof swiperCSSUrl !== 'undefined' ? swiperCSSUrl : '');

    return { jsUrl: jsUrl, cssUrl: cssUrl };
  }

  // Featurable Configuration & Styles
  const FEATURABLE_CONFIG = {
    styleId: 'featurable-store-detail-overrides',
    selectors: {
      container: '#featurable-container',
      body: '.widget__body',
      bgWhite: '[class*="Badge-module__bgWhite"]',
      branding: '[class*="Badge-module__branding"]',
      googleIcon: '.badge__google-icon',
      badgeContainer: '.badge__container',
      badgeInfo: '.badge__info',
      ratingContainer: '.badge__rating-container',
      starsContainer: '.badge__stars-container',
      starRating: '.star-rating',
      starFill: '.star-rating__fill',
      starEmpty: '.star-rating__empty',
      starItem: '[class*="BadgeStarRating-module__star__container"]',
      starSvg: '[class*="BadgeStarRating-module__star--"]',
      loading: '[class*="App-module__loadingContainer"]',
      errorClass: '[class*="App-module__errorClass"]'
    },
    getStyles: function() {
      const s = this.selectors;
      return `
        ${s.container} { padding: 0 !important; }
        ${s.body} { padding: 0 !important; min-width: min-content !important; width: 175px !important; }
        ${s.bgWhite} { background: transparent !important; }
        ${s.branding}, ${s.googleIcon}, ${s.loading} { display: none !important; }
        ${s.badgeContainer} { border: 0 !important; box-shadow: none !important; text-align: left !important; justify-content: flex-start !important; display: flex !important; align-items: center !important; padding: 0 !important; }
        ${s.badgeContainer} > div:first-child { display: none !important; }
        ${s.badgeInfo} { padding-left: 0 !important; display: flex !important; align-items: center !important; }
        ${s.badgeInfo} > :not(${s.ratingContainer}) { display: none !important; }
        .badge__link-container { display: none !important; }
        ${s.ratingContainer} { justify-content: flex-start !important; display: flex !important; align-items: center !important; flex-direction: row-reverse !important; gap: 8px !important; }
        ${s.starsContainer}, ${s.starRating}, ${s.starFill}, ${s.starEmpty}, ${s.starItem} { display: flex !important; align-items: center !important; gap: 4px !important; }
        ${s.starSvg} { width: 24px !important; height: 24px !important; }
        ${s.errorClass} { display: none !important; padding: 0 !important; }
      `;
    }
  };

  function ensureFeaturableScriptLoaded() {
    if (featurableScriptRequested) return;
    const existingScript = document.querySelector(`script[src="${FEATURABLE_SCRIPT_SRC}"]`);
    if (existingScript) {
      featurableScriptRequested = true;
      return;
    }

    // Lazy load Featurable script only when widget is needed, after page is fully loaded
    function loadFeaturableScript() {
      if (featurableScriptRequested) return;
      
      const script = document.createElement('script');
      script.src = FEATURABLE_SCRIPT_SRC;
      script.defer = true;
      script.async = true; // Add async for better performance
      script.charset = 'UTF-8';
      script.setAttribute('data-featurable-bundle', 'true');
      document.head.appendChild(script);
      featurableScriptRequested = true;
    }

    // Wait for page to be fully loaded before loading external script (heavy third-party script)
    if (document.readyState === 'complete') {
      // Use setTimeout to defer to next event loop cycle
      setTimeout(loadFeaturableScript, 100);
    } else {
      window.addEventListener('load', function() {
        setTimeout(loadFeaturableScript, 100);
      }, { once: true });
    }
  }

  function injectFeaturableShadowStyles(shadowRoot) {
    if (!shadowRoot || shadowRoot.querySelector(`style[data-featurable-custom="${FEATURABLE_CONFIG.styleId}"]`)) {
      return;
    }

    // Use requestAnimationFrame to batch DOM modifications
    requestAnimationFrame(() => {
      // Double-check after frame delay to prevent race conditions
      if (shadowRoot.querySelector(`style[data-featurable-custom="${FEATURABLE_CONFIG.styleId}"]`)) {
        return;
      }

      const style = document.createElement('style');
      style.setAttribute('data-featurable-custom', FEATURABLE_CONFIG.styleId);
      style.textContent = FEATURABLE_CONFIG.getStyles();
      shadowRoot.appendChild(style);
    });
  }

  // Hide Featurable "Failed to load reviews" errors
  // Optimized to only modify elements that aren't already hidden
  function fixFeaturableErrorMessages() {
    const widgets = document.querySelectorAll('[id^="featurable-"]');
    if (!widgets.length) return;

    widgets.forEach(widget => {
      // Check in shadow DOM (widget itself or any descendant with a shadowRoot)
      let shadowHost = null;
      if (widget.shadowRoot) {
        shadowHost = widget;
      } else {
        const allDescendants = widget.querySelectorAll('*');
        for (const el of allDescendants) {
          if (el.shadowRoot) {
            shadowHost = el;
            break;
          }
        }
      }

      // Hide error messages inside shadow DOM
      if (shadowHost && shadowHost.shadowRoot) {
        const errorElementsShadow = shadowHost.shadowRoot.querySelectorAll('[class*="App-module__errorClass"]');
        errorElementsShadow.forEach(errorEl => {
          // Only modify if not already hidden to prevent unnecessary mutations
          if (errorEl.style.display !== 'none') {
            const errorText = errorEl.textContent || errorEl.innerText;
            if (errorText && errorText.includes('Failed to load reviews')) {
              errorEl.style.display = 'none';
            }
          }
        });
      }

      // Also hide error messages in regular DOM (fallback)
      const errorElements = widget.querySelectorAll('[class*="App-module__errorClass"]');
      errorElements.forEach(errorEl => {
        // Only modify if not already hidden to prevent unnecessary mutations
        if (errorEl.style.display !== 'none') {
          const errorText = errorEl.textContent || errorEl.innerText;
          if (errorText && errorText.includes('Failed to load reviews')) {
            errorEl.style.display = 'none';
          }
        }
      });
    });
  }

  function applyFeaturableShadowStyles() {
    const wrappers = document.querySelectorAll('.shadow-wrapper');
    if (!wrappers.length) return;

    // Batch DOM operations to minimize mutations
    wrappers.forEach(wrapper => {
      if (wrapper.shadowRoot) {
        injectFeaturableShadowStyles(wrapper.shadowRoot);
      }
    });
  }

  // Store observer instance to prevent multiple observers
  let featurableObserver = null;
  let isProcessingMutations = false;

  function observeFeaturableWidgets() {
    // Prevent multiple observers
    if (featurableObserver) {
      return;
    }

    // Delay observer initialization until after page is fully loaded to reduce initial overhead
    function initObserver() {
      if (featurableObserver) return;

      // Initial application
      applyFeaturableShadowStyles();
      fixFeaturableErrorMessages();

      // Create observer with debouncing and guard to prevent infinite loops
      featurableObserver = new MutationObserver(() => {
        // Guard against recursive calls
        if (isProcessingMutations) {
          return;
        }

        isProcessingMutations = true;
        
        // Use requestAnimationFrame to batch DOM operations and prevent recursion
        requestAnimationFrame(() => {
          try {
            applyFeaturableShadowStyles();
            fixFeaturableErrorMessages();
          } finally {
            isProcessingMutations = false;
          }
        });
      });

      // Observe only the widget container area, not entire body
      const widgetContainer = document.getElementById('store-detail-featurable');
      const targetNode = widgetContainer || document.body;
      
      // Use more specific observation - only watch for widget-related changes
      featurableObserver.observe(targetNode, {
        childList: true,
        subtree: widgetContainer ? true : false, // Only subtree if watching container
        attributes: false,
        attributeOldValue: false
      });
    }

    // Wait for page to be fully loaded before starting observer (reduces initial performance impact)
    if (document.readyState === 'complete') {
      setTimeout(initObserver, 200);
    } else {
      window.addEventListener('load', function() {
        setTimeout(initObserver, 200);
      }, { once: true });
    }
  }

  /**
   * Get store handle from URL query parameter
   */
  function getHandleFromURL() {
    const pageUrl = window.location.href;
    const handle = pageUrl.split('?')[1];
    return handle ? decodeURIComponent(handle.trim()) : null;
  }

  /**
   * Get store data from localStorage
   */
  function getStoreFromLocalStorage() {
    try {
      const storeData = localStorage.getItem('selectedStore');
      
      if (storeData) {
        const parsed = JSON.parse(storeData);
        return parsed;
      }
      
      console.warn('No store data found in localStorage');
      return null;
    } catch (error) {
      console.error('Error reading store from localStorage:', error);
      return null;
    }
  }



function normalizeHandle(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/^\/+|\/+$/g, '');
}

function handleize(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function getStoresFromPageJson() {
  const dataScript =
    document.getElementById('store-detail-data') ||
    document.getElementById('store-locator-data');

  if (!dataScript || !dataScript.textContent.trim()) {
    return [];
  }

  try {
    const data = JSON.parse(dataScript.textContent);
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Error parsing embedded store data:', error);
    return [];
  }
}

function getStoreHandle(storeData) {
  if (!storeData) return '';

  return normalizeHandle(
    storeData.storeDetailsUrl ||
    storeData.bookAppointmentUrl ||
    storeData.handle ||
    handleize(storeData.storeDetailPageTitle || storeData.name)
  );
}

function findStoreByHandle(handle) {
  const targetHandle = normalizeHandle(handle);
  if (!targetHandle) return null;

  const stores = getStoresFromPageJson();

  return stores.find(function(store) {
    return getStoreHandle(store) === targetHandle;
  }) || null;
}

function saveResolvedStoreToLocalStorage(storeData, handle) {
  try {
    localStorage.setItem('selectedStore', JSON.stringify(storeData));
    localStorage.setItem('selectedStoreHandle', handle || getStoreHandle(storeData));

    if (storeData && storeData.storeId) {
      localStorage.setItem('store_id', storeData.storeId);
    }
  } catch (error) {
    console.warn('Could not save resolved store to localStorage:', error);
  }
}

function resolveStoreData() {
  const urlHandle = getHandleFromURL();
  const normalizedUrlHandle = normalizeHandle(urlHandle);

  // 1. Prefer fresh store data from Shopify embedded JSON
  if (normalizedUrlHandle) {
    const storeFromPageJson = findStoreByHandle(normalizedUrlHandle);

    if (storeFromPageJson) {
      // Update cache with the latest store data
      saveResolvedStoreToLocalStorage(
        storeFromPageJson,
        normalizedUrlHandle
      );

      return storeFromPageJson;
    }
  }

  // 2. Fallback to localStorage only if fresh page data is unavailable
  const cachedStore = getStoreFromLocalStorage();

  if (cachedStore) {
    const cachedHandle = normalizeHandle(
      localStorage.getItem('selectedStoreHandle') ||
      getStoreHandle(cachedStore)
    );

    if (!normalizedUrlHandle || cachedHandle === normalizedUrlHandle) {
      return cachedStore;
    }
  }

  return null;
}


  /**
   * Generate responsive image URLs with srcset
   * Supports Shopify CDN images and external URLs
   */
  function generateImageSrcset(imageUrl) {
    if (!isValidHttpUrl(imageUrl)) {
      return null;
    }

    // Check if it's a Shopify CDN image
    const isShopifyImage = imageUrl.includes('cdn.shopify.com') || imageUrl.includes('/cdn/shop/');
    
    if (isShopifyImage) {
      // Extract base URL and extension
      let baseUrl = imageUrl.split('?')[0];
      
      // Remove existing size suffix if present (e.g., _800x.jpg)
      baseUrl = baseUrl.replace(/_\d+x\.(jpg|jpeg|png|webp|avif)$/i, '');
      
      // Get extension
      const extensionMatch = baseUrl.match(/\.(jpg|jpeg|png|webp|avif)$/i);
      const ext = extensionMatch ? extensionMatch[1].toLowerCase() : 'jpg';
      
      // Remove extension to get true base
      const trueBase = baseUrl.replace(/\.(jpg|jpeg|png|webp|avif)$/i, '');
      
      // Generate srcset with different widths (optimized for store detail page)
      const widths = [375, 750, 800, 1200];
      const srcset = widths
        .map(width => `${trueBase}_${width}x.${ext} ${width}w`)
        .join(', ');
      
      return {
        srcset: srcset,
        src: `${trueBase}_800x.${ext}`, // Default fallback
        sizes: '(max-width: 749px) 100vw, (max-width: 989px) 100vw, 45vw'
      };
    } else {
      // For external images, use the original URL
      return {
        srcset: `${imageUrl} 800w`,
        src: imageUrl,
        sizes: '(max-width: 749px) 100vw, (max-width: 989px) 100vw, 45vw'
      };
    }
  }

  /**
   * Generate star rating HTML using star-active.liquid SVG
   */
  function generateStarRating(rating) {
    if (!rating) return '';
    
    const ratingNum = parseFloat(rating) || 0;
    const fullStars = Math.floor(ratingNum);
    
    // Active star SVG (from star-active.liquid)
    const activeStarSVG = '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="13" viewBox="0 0 14 13" fill="none"><path d="M6.49514 0.336808C6.65019 -0.112306 7.28536 -0.112306 7.4404 0.336809L8.75986 4.1588C8.82945 4.36038 9.01923 4.49564 9.23249 4.49564H13.4347C13.9264 4.49564 14.1228 5.13084 13.717 5.40837L10.3705 7.69673C10.1845 7.82388 10.1065 8.05969 10.1801 8.27262L11.4704 12.0104C11.6276 12.4657 11.1132 12.8582 10.7156 12.5863L7.25 10.2165C7.07984 10.1001 6.85571 10.1001 6.68555 10.2165L3.21997 12.5863C2.82231 12.8582 2.30791 12.4657 2.46511 12.0104L3.75549 8.27262C3.829 8.05969 3.75104 7.82388 3.56509 7.69673L0.218578 5.40837C-0.187276 5.13084 0.00913632 4.49564 0.500805 4.49564H4.70306C4.91632 4.49564 5.1061 4.36038 5.17569 4.1588L6.49514 0.336808Z" fill="#FDBC2A"/></svg>';
    
    // Inactive star SVG
    const inactiveStarSVG = '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="13" viewBox="0 0 14 13" fill="none"><path d="M6.49514 0.336808C6.65019 -0.112306 7.28536 -0.112306 7.4404 0.336809L8.75986 4.1588C8.82945 4.36038 9.01923 4.49564 9.23249 4.49564H13.4347C13.9264 4.49564 14.1228 5.13084 13.717 5.40837L10.3705 7.69673C10.1845 7.82388 10.1065 8.05969 10.1801 8.27262L11.4704 12.0104C11.6276 12.4657 11.1132 12.8582 10.7156 12.5863L7.25 10.2165C7.07984 10.1001 6.85571 10.1001 6.68555 10.2165L3.21997 12.5863C2.82231 12.8582 2.30791 12.4657 2.46511 12.0104L3.75549 8.27262C3.829 8.05969 3.75104 7.82388 3.56509 7.69673L0.218578 5.40837C-0.187276 5.13084 0.00913632 4.49564 0.500805 4.49564H4.70306C4.91632 4.49564 5.1061 4.36038 5.17569 4.1588L6.49514 0.336808Z" fill="#BBBBBB"/></svg>';
    
    let starsHtml = '';
    const totalStars = 5;
    
    // Generate active stars (1-5)
    for (let i = 0; i < totalStars; i++) {
      if (i < fullStars) {
        starsHtml += activeStarSVG;
      } else {
        starsHtml += inactiveStarSVG;
      }
    }
    
    return starsHtml;
  }

  /**
   * Set reviews section with count and rating
   * Format: [Google Icon] 107 Reviews ([star SVG] 4.5)
   */
 function setReviews(reviewCount, rating) {
  const reviewCountElement = document.getElementById('store-review-count');
  const ratingElement = document.getElementById('store-review-rating');

  // Star icon
  const starSVG = `
    <svg xmlns="http://www.w3.org/2000/svg"
         width="14"
         height="13"
         viewBox="0 0 14 13"
         fill="none"
         style="vertical-align: middle; margin-right: 4px;">
      <path d="M6.49514 0.336808C6.65019 -0.112306 7.28536 -0.112306 7.4404 0.336809L8.75986 4.1588C8.82945 4.36038 9.01923 4.49564 9.23249 4.49564H13.4347C13.9264 4.49564 14.1228 5.13084 13.717 5.40837L10.3705 7.69673C10.1845 7.82388 10.1065 8.05969 10.1801 8.27262L11.4704 12.0104C11.6276 12.4657 11.1132 12.8582 10.7156 12.5863L7.25 10.2165C7.07984 10.1001 6.85571 10.1001 6.68555 10.2165L3.21997 12.5863C2.82231 12.8582 2.30791 12.4657 2.46511 12.0104L3.75549 8.27262C3.82945 8.05969 3.7512 7.82388 3.56509 7.69673L0.218578 5.40837C-0.187276 5.13084 0.00913632 4.49564 0.500805 4.49564H4.70306C4.91632 4.49564 5.1061 4.36038 5.17569 4.1588L6.49514 0.336808Z"
            fill="#FDBC2A"/>
    </svg>
  `;

  // Rating: ⭐ 4.8
  if (ratingElement) {
    if (rating) {
      ratingElement.innerHTML = `${starSVG}<span>${rating}</span>`;
      ratingElement.style.display = 'inline-flex';
      ratingElement.style.alignItems = 'center';
    } else {
      ratingElement.innerHTML = '';
    }
  }

  // Review count: (163 reviews)
  if (reviewCountElement) {
    if (reviewCount) {
      reviewCountElement.textContent = `(${reviewCount} reviews)`;
      reviewCountElement.style.display = 'inline';
    } else {
      reviewCountElement.textContent = '';
    }
  }
}

  function setReviewLink(reviewLink) {
    const reviewLinkElement = document.getElementById('store-review-link');
    const reviewSeparator = document.getElementById('store-review-separator');

    if (!reviewLinkElement || !reviewSeparator) return;

    if (reviewLink) {
      // Handle both full URLs and Google Place IDs
      let finalUrl = reviewLink;
      if (!reviewLink.startsWith('http') && !reviewLink.startsWith('//')) {
        finalUrl = `https://search.google.com/local/writereview?placeid=${reviewLink}`;
      }
      
      reviewLinkElement.href = finalUrl;
      reviewLinkElement.textContent = 'Write a review';
      reviewLinkElement.style.display = '';
      reviewSeparator.style.display = '';
    } else {
      reviewLinkElement.style.display = 'none';
      reviewSeparator.style.display = 'none';
    }
  }

  function setFeaturableWidget(widgetId) {
    const widgetContainer = document.getElementById('store-detail-featurable');
    if (!widgetContainer) return;

    if (widgetId) {
      const safeId = String(widgetId).trim();
      if (!safeId) return;
      widgetContainer.classList.remove('hidden');
      widgetContainer.innerHTML = `<div id="featurable-${safeId}" data-featurable-async style="display: block;"></div>`;
      ensureFeaturableScriptLoaded();
      observeFeaturableWidgets();
      // Extra safety: hide any error messages shortly after widget init
      setTimeout(fixFeaturableErrorMessages, 800);
    } else {
      widgetContainer.classList.add('hidden');
      widgetContainer.innerHTML = '';
      if (featurableObserver) {
        featurableObserver.disconnect();
        featurableObserver = null;
      }
    }
  }

  /**
   * Set optimized image with picture element and srcset
   */
  function setOptimizedImage(imageUrl, altText) {
    const pictureElement = document.getElementById('store-image-picture');
    const imageElement = document.getElementById('store-image');
    const sourceDesktop = document.getElementById('store-image-source-desktop');
    const sourceTablet = document.getElementById('store-image-source-tablet');

    if (!pictureElement || !imageElement) {
      return;
    }

    if (!isValidHttpUrl(imageUrl)) {
      pictureElement.style.display = 'none';
      return;
    }

    const imageData = generateImageSrcset(imageUrl);

    if (!imageData) {
      pictureElement.style.display = 'none';
      return;
    }

    // Set alt text
    imageElement.alt = altText || 'Store image';

    // Set srcset for sources
    if (sourceDesktop && imageData.srcset) {
      sourceDesktop.srcset = imageData.srcset;
    }
    if (sourceTablet && imageData.srcset) {
      sourceTablet.srcset = imageData.srcset;
    }
// preload changes shanti-GL suggestion
// Preload LCP image
const existingPreload = document.querySelector(
  `link[rel="preload"][href="${imageData.src}"]`
);

if (!existingPreload) {
  const preload = document.createElement('link');
  preload.rel = 'preload';
  preload.as = 'image';
  preload.href = imageData.src;
  preload.imagesrcset = imageData.srcset;
preload.imagesizes = '(max-width:749px)100vw,(max-width:989px)100vw,45vw';

  if (imageData.srcset) {
    preload.setAttribute('imagesrcset', imageData.srcset);
    preload.setAttribute('imagesizes', imageData.sizes);
  }

  document.head.appendChild(preload);
}
    // Set main image src and srcset
    imageElement.src = imageData.src;
    if (imageData.srcset) {
      imageElement.srcset = imageData.srcset;
      imageElement.sizes = imageData.sizes;
    }

    // Update noscript fallback
    const noscriptImg = document.getElementById('store-image-noscript');
    if (noscriptImg) {
      noscriptImg.src = imageData.src;
      noscriptImg.alt = altText || 'Store image';
    }

    // Add loading event for smooth fade-in
    // Use requestAnimationFrame to prevent flickering during scroll
    if (imageElement.complete) {
      requestAnimationFrame(function() {
        imageElement.style.opacity = '1';
      });
    } else {
      imageElement.addEventListener('load', function() {
        requestAnimationFrame(function() {
          imageElement.style.opacity = '1';
        });
      }, { once: true });
      imageElement.addEventListener('error', function() {
        requestAnimationFrame(function() {
          imageElement.style.opacity = '1';
        });
        console.warn('Failed to load store image:', imageData.src);
      }, { once: true });
      // Set initial opacity for fade-in effect
      imageElement.style.opacity = '0';
    }

    // pictureElement.style.display = 'block';
  }

  /**
   * Create carousel slide HTML
   */
  function createCarouselSlide(imageUrl, altText, index) {
    const imageData = generateImageSrcset(imageUrl);
    if (!imageData) {
      return '';
    }

    const slide = document.createElement('div');
    slide.className = 'swiper-slide';
    
    const picture = document.createElement('picture');
    
    const sourceDesktop = document.createElement('source');
    sourceDesktop.media = '(min-width: 990px)';
    sourceDesktop.srcset = imageData.srcset;
    sourceDesktop.type = 'image/jpeg';
    
    const sourceTablet = document.createElement('source');
    sourceTablet.media = '(min-width: 750px)';
    sourceTablet.srcset = imageData.srcset;
    sourceTablet.type = 'image/jpeg';
    
    const img = document.createElement('img');
    img.src = imageData.src;
    img.srcset = imageData.srcset;
    img.sizes = imageData.sizes;
    img.alt = altText || `Store image ${index + 1}`;
    img.loading = index === 0 ? 'eager' : 'lazy';
    img.decoding = 'async';
    img.width = 800;
    img.height = 600;
    img.style.width = '100%';
    img.style.height = 'auto';
    img.style.display = 'block';
    img.style.objectFit = 'cover';
    
    picture.appendChild(sourceDesktop);
    picture.appendChild(sourceTablet);
    picture.appendChild(img);
    slide.appendChild(picture);
    
    return slide;
  }

  /**
   * Lazy load Swiper library only when carousel is needed.
   * This function is called only after confirming that storeImagesList has 2+ valid images.
   */
  function loadSwiperLibrary(callback) {
    if (typeof Swiper !== 'undefined') {
      if (callback) callback(true);
      return;
    }

    const assets = getSwiperAssetUrls();

    // Important: do not throw ReferenceError or load anything if Liquid did not provide asset URLs.
    if (!assets.jsUrl) {
      console.warn('Swiper JS URL is missing. Carousel script was not loaded.');
      if (callback) callback(false);
      return;
    }

    const existingScript = document.querySelector(`script[src="${assets.jsUrl}"], script[src*="swiper-bundle.min.js"]`);
    if (existingScript) {
      if (typeof Swiper !== 'undefined') {
        if (callback) callback(true);
        return;
      }

      existingScript.addEventListener('load', function() {
        if (callback) callback(typeof Swiper !== 'undefined');
      }, { once: true });
      return;
    }

    if (swiperScriptRequested) {
      return;
    }

    swiperScriptRequested = true;

    const existingCSS = assets.cssUrl
      ? document.querySelector(`link[href="${assets.cssUrl}"], link[href*="swiper-bundle.min.css"]`)
      : document.querySelector('link[href*="swiper-bundle.min.css"]');

    if (assets.cssUrl && !existingCSS) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = assets.cssUrl;
      document.head.appendChild(link);
    }

    const script = document.createElement('script');
    script.src = assets.jsUrl;
    script.defer = true;
    script.async = true;
    script.onload = function() {
      if (callback) callback(typeof Swiper !== 'undefined');
    };
    script.onerror = function() {
      swiperScriptRequested = false;
      console.error('Failed to load Swiper library');
      if (callback) callback(false);
    };
    document.head.appendChild(script);
  }

  /**
   * Initialize image carousel with Swiper.
   * Swiper loads only when storeImagesList metafield has more than one valid image.
   * If Store image list is blank/missing, only the single store image is used and no carousel assets are loaded.
   */
  function initializeImageCarousel(images, defaultImage, altText) {
    const carouselContainer = document.getElementById('store-detail__image-carousel');
    const singleImageContainer = document.getElementById('store-detail__single-image');
    const carouselWrapper = document.getElementById('store-carousel-wrapper');

    if (!carouselContainer || !carouselWrapper) {
      return;
    }

    const allImages = getValidStoreImages(images);
    const hasCarouselImages = allImages.length > 1;

    // No Store image list / only one image: do not load Swiper JS/CSS.
    if (!hasCarouselImages) {
      if (storeCarouselSwiper) {
        storeCarouselSwiper.destroy(true, true);
        storeCarouselSwiper = null;
      }
      stopCarouselPerfObserver();

      if (singleImageContainer) {
        singleImageContainer.classList.remove('hidden');
      }

      carouselContainer.classList.add('hidden');
      carouselWrapper.innerHTML = '';

      const imageToUse = allImages[0] || defaultImage || '';
      if (imageToUse) {
        setOptimizedImage(imageToUse, altText);
      }
      return;
    }

    // Store image list has 2+ valid images: render carousel and then load Swiper.
    if (singleImageContainer) {
      singleImageContainer.classList.add('hidden');
    }
    carouselContainer.classList.remove('hidden');
    carouselWrapper.innerHTML = '';

    allImages.forEach(function(imageUrl, index) {
      const slide = createCarouselSlide(imageUrl, altText, index);
      if (slide) {
        carouselWrapper.appendChild(slide);
      }
    });

    // Avoid Swiper loop issues when fewer than 2 slides were actually appended.
    if (carouselWrapper.children.length < 2) {
      carouselContainer.classList.add('hidden');
      if (singleImageContainer) {
        singleImageContainer.classList.remove('hidden');
      }
      setOptimizedImage(allImages[0] || defaultImage || '', altText);
      return;
    }

    loadSwiperLibrary(function(isLoaded) {
      if (!isLoaded || typeof Swiper === 'undefined') {
        console.warn('Swiper is not available. Showing first store image only.');
        carouselContainer.classList.add('hidden');
        if (singleImageContainer) {
          singleImageContainer.classList.remove('hidden');
        }
        setOptimizedImage(allImages[0] || defaultImage || '', altText);
        return;
      }

      requestAnimationFrame(function() {
        if (storeCarouselSwiper) {
          storeCarouselSwiper.destroy(true, true);
          storeCarouselSwiper = null;
        }
        stopCarouselPerfObserver();

        const carouselElement = carouselContainer.querySelector('.store-detail-carousel');
        if (!carouselElement) return;

        storeCarouselSwiper = new Swiper(carouselElement, {
          slidesPerView: 1,
          spaceBetween: 0,
          loop: allImages.length > 1,
          pagination: {
            el: '.store-detail-carousel__pagination',
            clickable: true
          },
          navigation: {
            nextEl: '.store-detail-carousel__next',
            prevEl: '.store-detail-carousel__prev'
          },
          lazy: {
            loadPrevNext: true
          }
        });
        startCarouselPerfObserver();
      });
    });
  }

  /**
   * Normalize store data into a structured store object
   */
  function normalizeStore(storeData) {
    if (!storeData) {
      return null;
    }

    // Map store data to expected format
    return {
      name: storeData.storeDetailPageTitle || storeData.name || 'Store',
      city: storeData.city || '',
      address: storeData.address || '',
      detailPageAddress: storeData.detailPageAddress || '',
      services: storeData.serviceTags || storeData.services || '',
      location: storeData.locationUrl || storeData.location || '',
      image: storeData.imageUrl || storeData.image || '',
      phone: storeData.phone || '',
      
      rating: storeData.rating || '',
      reviews: storeData.reviewCount || storeData.reviews || '',
      reviewLink: storeData.reviewLink || '',
      featurableWidgetId: storeData.featurableWidgetId || '',
      timing: storeData.openTiming || storeData.timing || '',
      days: storeData.openDays || storeData.days || '',
      openDayAndTime: storeData.openDayAndTime || '',
      appointment: storeData.bookAppointmentUrl || storeData.appointment || '',
      storeImagesList: storeData.storeImagesList || []
    };
  }

  /**
   * Parse timing string and check if store is currently open
   * Format: "Mon - Sat | 10am - 9pm"
   * Returns: { isOpen: boolean, isValidFormat: boolean }
   */
  function checkStoreStatus(timingString) {
    if (!timingString || typeof timingString !== 'string') {
      return { isOpen: false, isValidFormat: false };
    }

    // Check if format matches: "Day - Day | Time - Time"
    const formatPattern = /^([A-Za-z]{3})\s*-\s*([A-Za-z]{3})\s*\|\s*([\d]{1,2}(?:am|pm))\s*-\s*([\d]{1,2}(?:am|pm))$/i;
    const match = timingString.trim().match(formatPattern);

    if (!match) {
      return { isOpen: false, isValidFormat: false };
    }

    const [, startDay, endDay, openTime, closeTime] = match;

    // Day name mapping
    const dayMap = {
      'sun': 0, 'mon': 1, 'tue': 2, 'wed': 3, 'thu': 4, 'fri': 5, 'sat': 6
    };

    const startDayNum = dayMap[startDay.toLowerCase()];
    const endDayNum = dayMap[endDay.toLowerCase()];

    if (startDayNum === undefined || endDayNum === undefined) {
      return { isOpen: false, isValidFormat: false };
    }

    // Get current day and time
    const now = new Date();
    const currentDay = now.getDay();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentTimeInMinutes = currentHour * 60 + currentMinute;

    // Parse time strings (e.g., "10am", "9pm")
    function parseTime(timeStr) {
      const timeMatch = timeStr.match(/(\d{1,2})(am|pm)/i);
      if (!timeMatch) return null;

      let hours = parseInt(timeMatch[1], 10);
      const period = timeMatch[2].toLowerCase();

      if (period === 'pm' && hours !== 12) {
        hours += 12;
      } else if (period === 'am' && hours === 12) {
        hours = 0;
      }

      return hours * 60; // Return minutes since midnight
    }

    const openTimeInMinutes = parseTime(openTime);
    const closeTimeInMinutes = parseTime(closeTime);

    if (openTimeInMinutes === null || closeTimeInMinutes === null) {
      return { isOpen: false, isValidFormat: false };
    }

    // Check if current day is within the range
    let isDayInRange = false;
    if (startDayNum <= endDayNum) {
      // Normal range (e.g., Mon - Fri)
      isDayInRange = currentDay >= startDayNum && currentDay <= endDayNum;
    } else {
      // Wrapping range (e.g., Sat - Mon)
      isDayInRange = currentDay >= startDayNum || currentDay <= endDayNum;
    }

    if (!isDayInRange) {
      return { isOpen: false, isValidFormat: true };
    }

    // Check if current time is within opening hours
    let isTimeInRange = false;
    if (openTimeInMinutes <= closeTimeInMinutes) {
      // Normal time range (e.g., 10am - 9pm)
      isTimeInRange = currentTimeInMinutes >= openTimeInMinutes && currentTimeInMinutes < closeTimeInMinutes;
    } else {
      // Overnight hours (e.g., 10pm - 2am) - not typical for stores, but handle it
      isTimeInRange = currentTimeInMinutes >= openTimeInMinutes || currentTimeInMinutes < closeTimeInMinutes;
    }

    return { isOpen: isTimeInRange, isValidFormat: true };
  }

  /**
   * Open the shared booking drawer from the first available service product card.
   * Returns true if the drawer was opened, false otherwise.
   */
  function openStoreBookingDrawer() {
    var card = document.querySelector('.available-service-card');
    if (card && typeof openSharedDrawer === 'function') {
      openSharedDrawer(card);
      return true;
    }
    return false;
  }

  /**
   * Render store details to the DOM
   */
  function renderStoreDetails(store) {
    // Hide loader and show detail section in a single batch to prevent layout shifts
    requestAnimationFrame(function() {
      if (loaderElement) {
        loaderElement.classList.add('hidden');
      }

      if (detailElement) {
        detailElement.classList.remove('hidden');
      }
    });

    // Set store name
    const nameElement = document.getElementById('store-name');
    if (nameElement) {
      nameElement.textContent = store.name || 'Store';
    }

    // Set address (combine city and address)
    const addressElement = document.getElementById('store-address');
    if (addressElement) {
      const addressText = store.detailPageAddress
      const defaultAddressText = store.address;
      const formattedAddress = addressText.replace(/\n/g, '<span class="spacer"></span>');
      addressElement.innerHTML = formattedAddress || defaultAddressText || '';
    }

    // Set phone number (clickable)
    const phoneElement = document.getElementById('store-phone');
    const phoneLink = document.getElementById('store-phone-link');
    
    if (phoneElement && store.phone) {
      // Clean phone number for display and tel link
      const cleanPhone = store.phone.replace(/[\s\-\(\)]/g, '');
      phoneElement.textContent = store.phone;
      phoneElement.href = `tel:${cleanPhone}`;
      phoneElement.style.display = '';
    } else if (phoneElement) {
      phoneElement.style.display = 'none';
    }

    // Set phone button link
    if (phoneLink && store.phone) {
      const cleanPhone = store.phone.replace(/[\s\-\(\)]/g, '');
      phoneLink.href = `tel:${cleanPhone}`;
      phoneLink.style.display = '';
    } else if (phoneLink) {
      phoneLink.style.display = 'none';
    }

    // Set timing - use openDayAndTime from localStorage if available, otherwise fallback to days and timing
    const timingElement = document.getElementById('store-timing');
    let timingString = '';
    if (timingElement) {
      if (store.openDayAndTime) {
        timingString = store.openDayAndTime;
        timingElement.textContent = timingString;
      } else {
        // Fallback to previous method if openDayAndTime is not available
        const timingParts = [store.days, store.timing].filter(Boolean);
        timingString = timingParts.join(' | ') || '';
        timingElement.textContent = timingString;
      }
    }

    // Set open/closed status based on timing
    const openStatusElement = document.querySelector('.store-detail__open-status-text');
    const openStatusContainer = document.querySelector('.store-detail__open-status');
    if (openStatusElement && timingString) {
      const status = checkStoreStatus(timingString);
      if (status.isValidFormat) {
        openStatusElement.textContent = status.isOpen ? 'Open Now' : 'Closed';
        if (openStatusContainer) {
          openStatusContainer.style.display = '';
        }
      } else {
        // Hide status if format doesn't match
        if (openStatusContainer) {
          openStatusContainer.style.display = 'none';
        }
      }
    } else if (openStatusContainer) {
      // Hide if no timing string
      openStatusContainer.style.display = 'none';
    }

// Set reviews
const reviewCountElement = document.getElementById('store-review-count');
const ratingElement = document.getElementById('store-review-rating');

// Always show normal rating + review count
if (reviewCountElement) {
  reviewCountElement.style.display = '';
}

if (ratingElement) {
  ratingElement.style.display = '';
}

// Populate rating and review count
setReviews(store.reviews, store.rating);

// Do not show Google/Featurable review badge in this area
setFeaturableWidget('');

    // Initialize image carousel or single image
    initializeImageCarousel(store.storeImagesList, store.image, store.name || 'Store image');

    // Set location link
    const locationElement = document.getElementById('store-location');
    if (locationElement) {
      if (store.location) {
        locationElement.href = store.location;
        locationElement.style.display = '';
      } else {
        locationElement.style.display = 'none';
      }
    }

    // Set appointment link
    const appointmentElement = document.getElementById('store-appointment');
    if (appointmentElement) {
      appointmentElement.href = "javascript:void(0);";
      // On mobile/tablet, open booking drawer; on desktop, scroll to product carousel
      appointmentElement.addEventListener('click', function(e) {
        e.preventDefault();
        if (window.innerWidth < 990 && openStoreBookingDrawer()) {
          return;
        }
        scrollToProductCarousel();
      });
    }
  }

  /**
   * Scroll to the product carousel section smoothly
   */
  function scrollToProductCarousel() {
    const carouselElement = document.getElementById('availableServiceSectionCarousel');
    
    if (carouselElement) {
      // Calculate offset to account for any fixed headers (adjust if needed)
      const offset = 80; // Adjust this value based on your header height
      const elementPosition = carouselElement.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      // Smooth scroll to the carousel
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    } else {
      // If carousel is not found, try to find the section and scroll to it
      const sectionElement = document.querySelector('[data-available-service-section]');
      if (sectionElement) {
        const offset = 80;
        const elementPosition = sectionElement.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }
  }

  /**
   * Show error message
   */
  function showError(message) {
    if (loaderElement) {
      loaderElement.classList.add('hidden');
    }

    if (detailElement) {
      detailElement.classList.add('hidden');
    }

    if (notFoundElement) {
      notFoundElement.classList.remove('hidden');
      const errorText = notFoundElement.querySelector('p');
      if (errorText) {
        errorText.textContent = message || 'Store details not found.';
      }
    }

    console.error('Store Detail Error:', message);
  }

  /**
   * Initialize the store detail page
   */
  function init() {
    try {
      // Get DOM elements
      loaderElement = document.getElementById('store-detail-loader');
      detailElement = document.getElementById('store-detail');
      notFoundElement = document.getElementById('store-not-found');

      // If this JS is accidentally included globally, exit early on non-store-detail pages.
      if (!document.querySelector(SWIPER_SECTION_SELECTOR) && !detailElement && !loaderElement) {
        return;
      }
// FIX: Use the new resolver instead of reading only from localStorage.
// This prevents direct URL/incognito failures.
// resolveStoreData() first checks localStorage as a cache.
// If localStorage is empty or stale, it falls back to the URL handle
// and finds the matching store from the embedded Shopify metaobject JSON.
      // Get store data from localStorage
      const storeData = resolveStoreData();
// If no store is found even after both checks:
// 1. localStorage cache check
// 2. URL handle + embedded JSON fallback
// then show the not-found message.
// This usually means either:
// - store-detail-data JSON is missing from store-details-page.liquid,
// - the URL handle does not match storeDetailsUrl,
// - or the store metaobject data is unavailable.
      if (!storeData) {
        showError('Store details not found. Please select a store from the store locator.');
        return;
      }

      // Normalize and render
      const store = normalizeStore(storeData);
      
      if (!store) {
        showError('Store details not found.');
        return;
      }

      renderStoreDetails(store);

    } catch (error) {
      console.error('Error initializing store detail:', error);
      showError(error.message || 'An error occurred while loading store details. Please try again later.');
    }
  }

  // Initialize when DOM is ready (needed to render section from localStorage)
  // Heavy scripts (Swiper, Featurable) will be lazy loaded only when needed
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

